import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Customer,
  CustomerFilters,
  filterCustomers,
  getAuthHeaders,
  handleApiError,
  formatDate,
  getRelativeTime,
  formatPhoneNumber,
  getCustomerStats,
  getCustomerDisplayName,
  makeApiCall
} from '../utils/customerUtils'
import { PhoneLink } from './PhoneLink'

const PAGE_SIZE_DEFAULT = 25

interface CustomerListProps {
  onSelectCustomer?: (customer: Customer) => void
  onEditCustomer?: (customer: Customer) => void
  onDeleteCustomer?: (customer: Customer) => void
  showActions?: boolean
  maxCustomers?: number
}

export function CustomerList({ 
  onSelectCustomer, 
  onEditCustomer, 
  onDeleteCustomer, 
  showActions = true,
  maxCustomers 
}: CustomerListProps) {
  const { t } = useTranslation()
  const [customers, setCustomers] = useState<Customer[]>([])
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<Set<number>>(new Set())
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT)
  const [showAll, setShowAll] = useState(false)

  // Filters and sorting
  const [filters, setFilters] = useState<CustomerFilters>({
    search: '',
    sortBy: 'name',
    sortDirection: 'asc'
  })

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    recentlyAdded: 0,
    withPhone: 0,
    withAddress: 0
  })

  useEffect(() => {
    fetchCustomers()
  }, [])

  useEffect(() => {
    // Apply filters and sorting
    let filtered = filterCustomers(customers, filters)
    
    // Limit customers if specified
    if (maxCustomers) {
      filtered = filtered.slice(0, maxCustomers)
    }
    
    setFilteredCustomers(filtered)
    setStats(getCustomerStats(customers))
    // Reset to first page when filters change
    setCurrentPage(1)
  }, [customers, filters, maxCustomers])

  const fetchCustomers = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await makeApiCall('api/customers')

      if (!response.ok) {
        throw new Error('Failed to fetch customers')
      }

      const data = await response.json()
      setCustomers(data.customers || [])
    } catch (err) {
      console.error('Error fetching customers:', err)
      setError(handleApiError(err))
    } finally {
      setLoading(false)
    }
  }

  const handleSort = (field: CustomerFilters['sortBy']) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortDirection: prev.sortBy === field && prev.sortDirection === 'asc' ? 'desc' : 'asc'
    }))
  }

  const handleSearch = (searchTerm: string) => {
    setFilters(prev => ({
      ...prev,
      search: searchTerm
    }))
  }

  const handleDeleteCustomer = async (customer: Customer) => {
    if (!window.confirm(`Are you sure you want to delete customer "${getCustomerDisplayName(customer)}"? This action cannot be undone.`)) {
      return
    }

    setDeleting(prev => new Set(prev).add(customer.id))

    try {
      const response = await makeApiCall(`api/customers/${customer.id}`, {
        method: 'DELETE'
      })

      if (!response.ok) {
        throw new Error('Failed to delete customer')
      }

      // Remove customer from local state
      setCustomers(prev => prev.filter(c => c.id !== customer.id))

      if (onDeleteCustomer) {
        onDeleteCustomer(customer)
      }
    } catch (err) {
      console.error('Error deleting customer:', err)
      setError(handleApiError(err))
    } finally {
      setDeleting(prev => {
        const newSet = new Set(prev)
        newSet.delete(customer.id)
        return newSet
      })
    }
  }

  const getSortIcon = (field: CustomerFilters['sortBy']) => {
    if (filters.sortBy !== field) {
      return '↕'
    }
    return filters.sortDirection === 'asc' ? '↑' : '↓'
  }

  const copyToClipboard = async (text: string, type: 'email' | 'phone') => {
    try {
      await navigator.clipboard.writeText(text)
      // You could add a toast notification here if desired
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const formatPhoneForCopy = (phone: string): string => {
    // Remove all non-digit characters to get just the 10 digits
    return phone.replace(/\D/g, '')
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-300">{t('customers.table.loadingCustomers')}</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button 
            onClick={fetchCustomers}
            className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
          >
            {t('customers.table.retry')}
          </button>
        </div>
      </div>
    )
  }

  const totalPages = showAll ? 1 : Math.ceil(filteredCustomers.length / pageSize)
  const paginatedCustomers = showAll
    ? filteredCustomers
    : filteredCustomers.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="space-y-4">
      {/* Customer Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Search Bar */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1 max-w-md">
              <input
                type="text"
                placeholder={t('customers.table.searchPlaceholder')}
                value={filters.search || ''}
                onChange={(e) => handleSearch(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
              />
            </div>
            {filteredCustomers.length > 0 && (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {showAll
                  ? `${filteredCustomers.length} contacts`
                  : `${Math.min((currentPage - 1) * pageSize + 1, filteredCustomers.length)}–${Math.min(currentPage * pageSize, filteredCustomers.length)} of ${filteredCustomers.length}`
                }
              </div>
            )}
          </div>
        </div>

        {filteredCustomers.length === 0 ? (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            {filters.search
              ? t('customers.table.noCustomersMatching', { search: filters.search })
              : t('customers.table.noCustomersFound')
            }
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('name')}
                  >
                    {t('customers.table.name')} <span className="text-xs">{getSortIcon('name')}</span>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('email')}
                  >
                    {t('customers.table.email')} <span className="text-xs">{getSortIcon('email')}</span>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t('customers.table.phone')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {paginatedCustomers.map((customer) => (
                  <tr
                    key={customer.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${onSelectCustomer ? 'cursor-pointer' : ''}`}
                    onClick={() => onSelectCustomer && onSelectCustomer(customer)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-9 h-9 bg-indigo-100 dark:bg-indigo-900/40 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                          <span className="text-indigo-700 dark:text-indigo-300 text-sm font-semibold">
                            {getCustomerDisplayName(customer).split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </span>
                        </div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {getCustomerDisplayName(customer)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700 dark:text-gray-300">{customer.email}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); copyToClipboard(customer.email, 'email') }}
                          className="text-gray-300 hover:text-gray-600 dark:text-gray-600 dark:hover:text-gray-300 transition-colors opacity-0 group-hover:opacity-100"
                          title="Copy email"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {customer.phone ? (
                        <div className="flex items-center gap-2">
                          <PhoneLink
                            phoneNumber={customer.phone}
                            customerId={customer.id}
                            customerName={getCustomerDisplayName(customer)}
                            showIcon={false}
                          />
                          <button
                            onClick={(e) => { e.stopPropagation(); customer.phone && copyToClipboard(formatPhoneForCopy(customer.phone), 'phone') }}
                            className="text-gray-300 hover:text-gray-600 dark:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            title="Copy phone"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500 text-sm">{t('customers.table.noPhone')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination footer */}
        {!maxCustomers && filteredCustomers.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
              <span>
                {showAll
                  ? `All ${filteredCustomers.length} contacts`
                  : `Page ${currentPage} of ${totalPages} · ${filteredCustomers.length} contacts`
                }
              </span>
              <button
                onClick={() => { setShowAll(v => !v); setCurrentPage(1) }}
                className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
              >
                {showAll ? `Show ${PAGE_SIZE_DEFAULT} per page` : 'Show all'}
              </button>
            </div>

            {!showAll && totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >«</button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >‹</button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let page: number
                  if (totalPages <= 5) {
                    page = i + 1
                  } else if (currentPage <= 3) {
                    page = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i
                  } else {
                    page = currentPage - 2 + i
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                        currentPage === page
                          ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 border-gray-900 dark:border-gray-100 font-medium'
                          : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {page}
                    </button>
                  )
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >›</button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >»</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Load More for limited views */}
      {maxCustomers && customers.length > maxCustomers && (
        <div className="text-center">
          <a
            href="/customers"
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            {t('customers.table.viewAll', { count: customers.length })}
          </a>
        </div>
      )}
    </div>
  )
} 